/**
 * Sets a cookie.
 * Crafts the cookie string and leaves it within the browser's memory.
 */
export function setCookie(
  name: string,
  value: string,
  options: {
    path?: string;
    domain?: string;
    expires?: Date;
    secure?: boolean;
    sameSite?: 'Lax' | 'Strict' | 'None';
  } = {},
): void {
  const {
    path = '/', domain, expires, secure, sameSite,
  } = options;

  let str = `${name}=${encodeURIComponent(value)}`;
  if (path) { str += `; path=${path}`; }
  if (domain) { str += `; domain=${domain}`; }
  if (expires) { str += `; expires=${expires.toUTCString()}`; }
  if (secure) { str += '; secure'; }
  if (sameSite) { str += `; samesite=${sameSite}`; }

  document.cookie = str;
}

/**
 * Retrieves a cookie.
 * Returns its value or null when the trace has vanished.
 */
export function getCookie(name: string): string | null {
  const pattern = new RegExp(`(?:^|; )${name}=([^;]*)`);
  const match = document.cookie.match(pattern);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Deletes a cookie on all possible domain levels for current hostname.
 * Tries with domain `.sub.example.com`, `.example.com`, etc., and without domain.
 */
export function deleteCookie(
  name: string,
  options: {
    path?: string;
  } = {},
): void {
  const { path = '/' } = options;

  const { hostname } = window.location; // e.g. 'app.stage.openedx.io'
  const parts = hostname.split('.');

  const expire = 'Thu, 01 Jan 1970 00:00:00 GMT';

  // Try for all domain levels: .app.stage.openedx.io, .stage.openedx.io, .openedx.io
  for (let i = 0; i < parts.length - 1; i++) {
    const domain = `.${parts.slice(i).join('.')}`;
    document.cookie = `${name}=; expires=${expire}; path=${path}; domain=${domain}`;
  }

  // Also try without domain (host-only cookie)
  document.cookie = `${name}=; expires=${expire}; path=${path}`;
}
